import { useEffect, useState, useRef, useCallback } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { useRouter } from 'next/router';
import cookiesConstants from '@/constants/cookies.constants';
import { getCookie } from 'cookies-next';

const FIRST_VISIT_COOKIE = cookiesConstants.COOKIES.FIRST_VISIT_DONE.name;

export function useAnalytics() {
  const [hasMounted, setHasMounted] = useState(false);
  const [isFirstVisit, setIsFirstVisit] = useState(false);
  const [utmParams, setUtmParams] = useState<Record<string, string>>({});
  const [pageParams, setPageParams] = useState({
    page_location: '',
    page_referrer: '',
    page_title: '',
  });
  const batchOrderingId = useRef(0);
  const batchPageId = useRef(Date.now());
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();

  // Extract SKU slug if present in the route (for product pages)
  let skuSlug: string | undefined = undefined;
  if (router && router.query && router.query.slug) {
    if (Array.isArray(router.query.slug)) {
      skuSlug = router.query.slug.join('/');
    } else {
      skuSlug = router.query.slug as string;
    }
  }

  useEffect(() => {
    setHasMounted(true);
  }, []);

  useEffect(() => {
    if (hasMounted) {
      setIsFirstVisit(getCookie(FIRST_VISIT_COOKIE) !== '1');
    }
  }, [hasMounted]);

  // Update UTM and page params on route change
  useEffect(() => {
    // UTM params
    setUtmParams({
      campaign: searchParams.get('utm_campaign') || '',
      campaign_id: searchParams.get('utm_id') || '',
      content: searchParams.get('utm_content') || '',
      medium: searchParams.get('utm_medium') || '',
      source: searchParams.get('utm_source') || '',
      term: searchParams.get('utm_term') || '',
    });
    // Page params
    setPageParams({
      page_location: window.location.href,
      page_referrer: document.referrer,
      page_title: document.title,
    });
  }, [pathname, searchParams]);

  // Set first visit cookie
  const setFirstVisitCookie = useCallback(() => {
    document.cookie = `${FIRST_VISIT_COOKIE}=1; path=/; max-age=31536000`;
    setIsFirstVisit(false);
  }, []);

  // Device category
  const getDeviceCategory = useCallback(() => {
    const ua = navigator.userAgent;
    if (/mobile/i.test(ua)) return 'mobile';
    if (/tablet/i.test(ua)) return 'tablet';
    return 'desktop';
  }, []);

  // Batch IDs
  const getNextBatchOrderingId = useCallback(() => {
    batchOrderingId.current += 1;
    return batchOrderingId.current;
  }, []);
  const getBatchPageId = useCallback(() => batchPageId.current, []);

  // GA session info
  const getGASessionId = useCallback(() => {
    if (typeof document === 'undefined') return '';
    const match = document.cookie.match(/_ga=GA\d\.\d\.(\d+)/);
    return match ? match[1] : '';
  }, []);
  const getGASessionNumber = useCallback(() => {
    if (typeof document === 'undefined') return '';
    // The _ga cookie format is usually _ga=GA1.1.XXXXXXXXX.Y, where Y is the session number
    const match = document.cookie.match(/_ga=GA\d+\.\d+\.\d+\.(\d+)/);
    return match ? match[1] : '';
  }, []);
  /**
   * Determines the type of page based on the URL pathname
   * @param {string} pathname - The current URL path
   * @returns {string} The page type identifier
   */
  const getPageType = useCallback((): string => {
    // Product pages
    if (pathname.startsWith('/sku/')) return 'product';

    // Dashboard pages
    if (pathname.startsWith('/dashboard')) return 'dashboard';

    // Authentication pages
    if (pathname.startsWith('/auth/')) return 'auth';

    // Content pages
    if (pathname.startsWith('/blog/')) return 'blog';
    if (pathname.startsWith('/collections/')) return 'collection';

    // Category and filter pages
    if (pathname.startsWith('/brands/')) return 'sku_brand';
    if (pathname.startsWith('/category/')) return 'sku_category';
    if (pathname.startsWith('/country/')) return 'sku_country';
    if (pathname.startsWith('/region/')) return 'sku_region';
    if (pathname.startsWith('/search/')) return 'sku_search';

    // Static pages
    if (pathname === '/about') return 'about';
    if (pathname === '/help') return 'help';
    if (pathname === '/how-it-works') return 'how-it-works';
    if (pathname === '/terms') return 'terms';
    if (pathname === '/privacy') return 'privacy';
    if (pathname === '/imprint') return 'imprint';

    // Default fallback
    return 'other';
  }, [pathname]);

  return {
    isFirstVisit,
    setFirstVisitCookie,
    utmParams,
    pageParams,
    getDeviceCategory,
    getNextBatchOrderingId,
    getBatchPageId,
    getGASessionId,
    getGASessionNumber,
    skuSlug,
    getPageType,
  };
}
